import { SetStateAction } from "react";
import { TFunction } from "react-i18next";

export function validateEmail(email: string): boolean {
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    return emailRegex.test(email);
}

export function isGuestInvozioEmail(email: string): boolean {
    const lowerEmail = email.toLowerCase();
    return lowerEmail.startsWith('guest') && lowerEmail.endsWith('@invozio.com');
}

export function validatePassword(password: string): boolean {
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[A-Za-z\d@$!%*?&]{8,}$/;
    return passwordRegex.test(password);
}
export const validatePostalCode = (value: string) => {
    return /^[A-Za-z0-9\s-]{3,10}$/.test(value);
};

export function validatePhoneNumber(phoneNumber: string): boolean {
    const cleanPhone = phoneNumber.replace(/\D/g, '');
    
    if (!/^\d+$/.test(cleanPhone)) {
        return false;
    }
    
    if (cleanPhone.length < 8 || cleanPhone.length > 13) {
        return false;
    }
    
    return true;
}

// export function validateFullName(fullName: string): boolean {
//     const nameRegex = /^[A-Za-zÅÄÖåäö][A-Za-zÅÄÖåäö0-9\s]*$/;;
//     const trimmedName = fullName.trim();

//     // Check if valid characters and minimum length
//     if (!nameRegex.test(trimmedName) || trimmedName.length < 2) {
//         return false;
//     }

//     // Check that name doesn't start with a number
//     const startsWithNumber = /^\d/.test(trimmedName);
//     return !startsWithNumber;
// }

export function validateLatinCharacters(text: string): boolean {
    const allowedPattern = /^[A-Za-zÅÄÖåäö][A-Za-zÅÄÖåäö0-9\s]*$/;
    return allowedPattern.test(text);
}

export function validateSSN(ssn: string): boolean {
    if (ssn && ssn.length === 12) {
        const last4 = ssn.slice(-4);
        return last4 !== "0000";
    }
    return true;
}

export function handleSSNInput(value: string): string {
    const cleaned = value.replace(/[^a-zA-Z0-9]/g, "");
    const maxLength = 12;
    return cleaned.length > maxLength ? cleaned.slice(0, maxLength) : cleaned;
}

export function handlePhoneInput(value: string): string {
    const cleaned = value.replace(/[^0-9]/g, '');
    return cleaned.slice(0, 13);
}

export function handleDOBInput(value: string): string {
    let cleaned = value.replace(/\D/g, '');

    cleaned = cleaned.slice(0, 8);
    if (cleaned.length > 8) {
    }

    if (cleaned.length === 8) {
        cleaned = cleaned + '000';
    }

    return cleaned;
}


export function validateEmailForSignin(email: string, t: (key: string) => string): string {
    if (!email) {
        return t("Email is required");
    }
    if (!validateEmail(email)) {
        return t("Invalid email address");
    }
    if (isGuestInvozioEmail(email)) {
        return t("GUEST_EMAIL_NOT_ALLOWED");
    }
    return '';
}

export function validatePasswordForSignin(password: string, t: (key: string) => string): string {
    if (!password) {
        return t("Password is required");
    }
    return '';
}

export function validateSigninForm(formData: { email: string; password: string }, t: (key: string) => string) {
    const emailError = validateEmailForSignin(formData.email, t);
    const passwordError = validatePasswordForSignin(formData.password, t);

    return {
        errors: {
            email: emailError,
            password: passwordError
        },
        isValid: !emailError && !passwordError
    };
}

export function handleSocialLoginAction(key: string, setShowBankIDModal: (show: boolean) => void) {
    switch (key) {
        case "bankid":
            setShowBankIDModal(true);
            break;
        default:
            break;
    }
} 


interface FormErrors {
    [key: string]: string;
}
    // NOTE: This function stores translation keys (not translated strings) so errors
// stay reactive when the language changes. Translate keys at render time with t(key).
export const validateForm = (formData: { firstName: string; lastName: string; email: string; password: string; noIdNumber: any; address: string; country: string ,socialSecurityNumber: string | any[]; dateOfBirth: string | any[]; agreeToTerms: any; phoneNo?: string; }, _t: (arg0: string) => string, setErrors: (errors: FormErrors) => void): boolean => {
    const newErrors: FormErrors = {};

    // Full name validation
    if (!formData.firstName) {
        newErrors.firstName = "FULL_NAME_REQUIRED";
    } else if (!validateLatinCharacters(formData.firstName)) {
        newErrors.firstName = "FULL_NAME_LATIN_ONLY";
    } 
    if (!formData.lastName) {
        newErrors.lastName = "LAST_NAME_REQUIRED";
    } else if (!validateLatinCharacters(formData.lastName)) {
        newErrors.lastName = "FULL_NAME_LATIN_ONLY";
    } 
    // Email validation
    if (!formData.email.trim()) {
        newErrors.email = "EMAIL_REQUIRED";
    } else if (!validateEmail(formData.email)) {
        newErrors.email = "EMAIL_INVALID";
    } else if (isGuestInvozioEmail(formData.email)) {
        newErrors.email = "GUEST_EMAIL_NOT_ALLOWED";
    }

    // Password validation
    if (!formData.password) {
        newErrors.password = "PASSWORD_REQUIRED";
    } else if (!validatePassword(formData.password)) {
        newErrors.password = "PASSWORD_RULE";
    }
    if (!formData.phoneNo && typeof formData.phoneNo === 'string') {
        newErrors.phoneNo = "INVALID_PHONE_NUMBER";
        if (!validatePhoneNumber(formData.phoneNo)) {
            newErrors.phoneNo = "INVALID_PHONE_NUMBER";
        }
    }
    if (!formData.address || !formData.address.trim()) {
        newErrors.address = "ADDRESS_REQUIRED";
    }
    if (!formData.country && typeof formData.country === 'string') {
        newErrors.country = "COUNTRY_REQUIRED";
    }

    if (!formData.noIdNumber && (!formData.socialSecurityNumber || formData.socialSecurityNumber.length < 12)) {
        newErrors.socialSecurityNumber = "SSN_NUMBER_MUST_HAVE_12_DIGITS";
    }

    if (!formData.noIdNumber && typeof formData.socialSecurityNumber === 'string' && !validateSSN(formData.socialSecurityNumber)) {
        newErrors.socialSecurityNumber = "SSN_LAST_4_DIGITS_CANNOT_BE_ALL_ZEROS";
    }

    if (formData.noIdNumber && (!formData.dateOfBirth || formData.dateOfBirth.length !== 11)) {
        newErrors.dateOfBirth = "DOB_MUST_HAVE_8_DIGITS.";
    }


    if (!formData.agreeToTerms) {
        newErrors.agreeToTerms = "PLEASE_SELECT_TERMS_AND_CONDITION";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
};


export const handleFormValidation = (formData: { email: string; password: string; rememberMe?: boolean; }, t: TFunction<"translation", undefined>, setErrors: { (value: SetStateAction<{ email: string; password: string; }>): void; (arg0: { email: string; password: string; }): void; }) => {
    const { errors: validationErrors, isValid } = validateSigninForm(formData, t);
    setErrors(validationErrors);
    return isValid;
};